import type {
  EditorView,
  PMNode,
  Schema,
  Slice,
} from '@bangle.io/prosemirror-plugins';

/**
 * Serializes clipboard content to Markdown using the same serializer as
 * durable saves.
 *
 * Prefer {@link serializeClipboardMarkdown} when an EditorView is available —
 * `doc.cut` preserves marks the same way as
 * `PmEditorService.getSelectionMarkdown`. This helper is the fallback for
 * drag payloads whose slice is not the live selection.
 */
export function serializeSliceAsMarkdown(
  slice: Slice,
  schema: Schema,
  serialize: (doc: PMNode) => string,
): string {
  if (slice.content.size === 0) {
    return '';
  }

  try {
    return serialize(schema.topNodeType.create(null, slice.content));
  } catch {
    const paragraphType = schema.nodes.paragraph;
    if (!paragraphType) {
      return slice.content.textBetween(0, slice.content.size, '\n\n');
    }
    try {
      return serialize(
        schema.topNodeType.create(
          null,
          paragraphType.create(null, slice.content),
        ),
      );
    } catch {
      return slice.content.textBetween(0, slice.content.size, '\n\n');
    }
  }
}

/**
 * Ctrl+C/X path: when the clipboard slice matches the live selection, cut the
 * selected range from the document so marks and block types match the
 * "Copy Selection as Markdown" command. Otherwise serialize the provided slice
 * (drag handle / non-selection copies).
 */
export function serializeClipboardMarkdown(
  slice: Slice,
  view: EditorView,
  serialize: (doc: PMNode) => string,
): string {
  const { from, to, empty } = view.state.selection;
  if (!empty) {
    const selectionSlice = view.state.selection.content();
    if (
      selectionSlice.openStart === slice.openStart &&
      selectionSlice.openEnd === slice.openEnd &&
      selectionSlice.content.eq(slice.content)
    ) {
      return serialize(view.state.doc.cut(from, to));
    }
  }
  return serializeSliceAsMarkdown(slice, view.state.schema, serialize);
}
